import React from 'react';
import dayjs from 'dayjs';
import i18n from 'i18next';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import {isMobile} from 'react-device-detect';
import {DatepickerDateType, ErrorType, InputEventType} from 'utils/types';
import {SelectRefType} from '@guestapp/old-ui-kit';
import {FieldErrorMessage} from '@chekinapp/ui';
import {
  DEVICE,
  TooltipInfo,
  useOutsideClick,
  useScreenResize,
} from '@chekin/guestapp-ui-kit';
import {SelectOption, parseDate} from '@chekin/core';
import {useEvent} from '../../../hooks';
import {
  DateInput,
  DateSelect,
  DateSelectWrapper,
  FieldsWrapper,
  LabelWrapper,
  Wrapper,
  DayInputWrapper,
  FieldsetWrapperStyled,
  YearInputWrapper,
} from './styled';

export type DatepickerHandle = {
  getDayValue: () => string;
  getYearValue: () => string;
  setDayValue: (value: string) => void;
  setYearValue: (value: string) => void;
};

const DAY_PATTERN = /^(([0]?[1-9])|([1-2][0-9])|(3[01]))$/;
const YEAR_PATTERN = /^(19|20)\d{2}$/;

const MONTHS = () => ({
  january: {
    label: i18n.t('january'),
    value: '1',
  },
  february: {
    label: i18n.t('february'),
    value: '2',
  },
  march: {
    label: i18n.t('march'),
    value: '3',
  },
  april: {
    label: i18n.t('april'),
    value: '4',
  },
  may: {
    label: i18n.t('may'),
    value: '5',
  },
  june: {
    label: i18n.t('june'),
    value: '6',
  },
  july: {
    label: i18n.t('july'),
    value: '7',
  },
  august: {
    label: i18n.t('august'),
    value: '8',
  },
  september: {
    label: i18n.t('september'),
    value: '9',
  },
  october: {
    label: i18n.t('october'),
    value: '10',
  },
  november: {
    label: i18n.t('november'),
    value: '11',
  },
  december: {
    label: i18n.t('december'),
    value: '12',
  },
});

function getNextMonth(monthIndex = 0) {
  const monthNumber = monthIndex + 1;
  return Object.values(MONTHS()).find(m => m.value === String(monthNumber));
}

type DatePickerValue = {
  day?: string | number | null;
  month?: SelectOption | null;
  year?: string | number | null;
};

type DatepickerProps = {
  label?: string;
  defaultValues?: DatePickerValue;
  onChange: (value: DatepickerDateType, name?: string) => void;
  onHandleKeyDown?: (event: React.KeyboardEvent) => void;
  invalid?: boolean;
  value?: DatepickerDateType;
  error?: ErrorType;
  disabled?: boolean;
  showDefaultMonth?: boolean;
  name?: string;
  withTooltip?: boolean;
  tooltipContent?: string | React.ReactNode;
  className?: string;
  onFocus?: () => void;
  onFieldFocus?: (fieldType: 'day' | 'year') => void;
  onFieldBlur?: (fieldType: 'day' | 'year') => void;
};

const Datepicker = React.forwardRef<DatepickerHandle, DatepickerProps>(
  (
    {
      label,
      defaultValues,
      onChange,
      invalid,
      value,
      error,
      disabled,
      name,
      withTooltip,
      tooltipContent,
      showDefaultMonth,
      className,
      onHandleKeyDown,
      onFocus,
      onFieldFocus,
      onFieldBlur,
    },
    ref,
  ) => {
    const {isMatch} = useScreenResize(DEVICE.laptop);
    const isMobileMode = isMatch || isMobile;
    const onChangeEvent = useEvent(onChange);

    const getDefaultMonth = () => {
      if (defaultValues?.month) {
        return defaultValues.month;
      }
      if (showDefaultMonth) {
        return Object.values(MONTHS).find(option => {
          return option?.value === (today.getMonth() + 1).toString();
        });
      }
      return undefined;
    };
    const getDefaultYear = () => {
      if (defaultValues?.year) {
        return defaultValues.year;
      }
      if (showDefaultMonth) {
        return today.getFullYear();
      }
      return undefined;
    };

    const {t} = useTranslation();
    const today = new Date();
    const [day, setDay] = React.useState(defaultValues?.day);
    const [month, setMonth] = React.useState<SelectOption | undefined | null>(
      getDefaultMonth(),
    );
    const [year, setYear] = React.useState(getDefaultYear());
    const [isInvalidDate, setIsInvalidDate] = React.useState(false);
    const [isYearInvalid, setIsYearInvalid] = React.useState(false);
    const stringValue = value?.toString();
    const isMMddYYYYFormat = /*ipDetails?.country_code === COUNTRY_CODES.usa;*/ false;
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const usaSelectRef = React.useRef<SelectRefType>(null);
    const inputYearRef = React.useRef<HTMLInputElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const isInvalid = invalid || Boolean(error) || isInvalidDate;

    useOutsideClick(wrapperRef, () => {
      setIsFocused(false);
    });

    const isEmpty = React.useMemo(() => {
      return !day && !month && !year;
    }, [day, month, year]);

    React.useEffect(() => {
      const currentRef = !isMMddYYYYFormat ? inputRef : usaSelectRef;
      if (isFocused) {
        currentRef.current?.focus();
      }
    }, [isFocused, inputRef, usaSelectRef, isMMddYYYYFormat]);

    React.useEffect(() => {
      function setValue(date: Date) {
        const isValidValueDate = dayjs(date).isValid();

        if (isValidValueDate) {
          const day = dayjs(date).date();
          setDay(String(day));

          const month = dayjs(date).month();
          setMonth(getNextMonth(month));

          const year = dayjs(date).year();
          if (!YEAR_PATTERN.test(String(year))) {
            onChangeEvent(undefined, name);
          } else {
            setYear(String(year));
          }
        }
      }

      if (stringValue) {
        const date = parseDate(stringValue);
        setValue(date);
      }
    }, [name, onChangeEvent, stringValue]);

    React.useEffect(
      function resetDateFields() {
        if (value === null) {
          setDay(undefined);
          setMonth(null);
          setYear(undefined);
        }
      },
      [value],
    );

    React.useEffect(
      function updateMonthTranslationOnLanguageChange() {
        setMonth(prevMonth => {
          if (!prevMonth) {
            return prevMonth;
          }

          const updatedTranslationMonth = Object.values(MONTHS()).find(month => {
            return month.value === prevMonth.value;
          });

          return updatedTranslationMonth;
        });
      },
      [t],
    );

    const handleChange = React.useCallback(
      (params: DatePickerValue) => {
        const day = params?.day;
        const monthValue = params?.month?.value;
        const year = params?.year;

        if (!day || !monthValue || !year) {
          onChangeEvent(undefined, name);
          return;
        }

        if (!YEAR_PATTERN.test(String(year))) {
          setIsYearInvalid(true);
          onChangeEvent(undefined, name);
          return;
        } else {
          setIsYearInvalid(false);
        }

        const date = new Date(`${monthValue}/${day}/${year}`);
        if (dayjs(date).isValid()) {
          setIsInvalidDate(false);
          onChangeEvent(date, name);
        } else {
          setIsInvalidDate(true);
          onChangeEvent(undefined, name);
        }
      },
      [name, onChangeEvent],
    );

    const handleDayChange = React.useCallback(
      ({target}: InputEventType) => {
        const value = target.value;
        if (value !== '' && !DAY_PATTERN.test(value)) {
          return;
        }

        setDay(value);
        handleChange({
          day: value,
          month,
          year,
        });
      },
      [handleChange, month, year],
    );

    const handleYearChange = React.useCallback(
      ({target}: InputEventType) => {
        const value = target.value;
        if (value.length > 4) {
          return;
        }

        setYear(value);
        handleChange({
          year: value,
          day,
          month,
        });
      },
      [day, handleChange, month],
    );

    const handleMonthChange = React.useCallback(
      (option: SelectOption | null) => {
        setMonth(option);
        handleChange({
          month: option,
          day,
          year,
        });
      },
      [day, handleChange, year],
    );

    React.useImperativeHandle(
      ref,
      () => ({
        getDayValue: () => String(day || ''),
        getYearValue: () => String(year || ''),
        setDayValue: (value: string) => {
          if (value !== '' && !DAY_PATTERN.test(value)) {
            return;
          }
          setDay(value);
          handleChange({
            day: value,
            month,
            year,
          });
        },
        setYearValue: (value: string) => {
          if (value.length > 4) {
            return;
          }
          setYear(value);
          handleChange({
            year: value,
            day,
            month,
          });
        },
      }),
      [day, month, year, handleChange],
    );

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (disabled) return;
      onHandleKeyDown?.(event);
    };

    const handleWrapperClick = React.useCallback(() => {
      if (isEmpty) {
        setIsFocused(true);
        onFocus?.();
      }
    }, [setIsFocused, isEmpty, onFocus]);

    return (
      <>
        <Wrapper
          aria-label={name}
          disabled={disabled}
          ref={wrapperRef}
          onClick={handleWrapperClick}
          className={clsx(className, 'datepicker', {
            'datepicker--focused': isFocused,
            'datepicker--disabled': disabled,
            'datepicker--empty': isEmpty,
            'datepicker--invalid': !!error,
          })}
        >
          <LabelWrapper
            onClick={() => setIsFocused(!isFocused)}
            className="datepicker__label kiosko:h-input"
            $isFocused={isFocused}
            $isEmpty={isEmpty}
            $disabled={disabled}
          ></LabelWrapper>
          <FieldsetWrapperStyled
            invalid={!!error}
            isFocused={isFocused}
            isEmpty={isEmpty}
            isActivated={!isEmpty || isFocused}
            label={label}
          />
          {withTooltip && (
            <TooltipInfo
              className="absolute -right-6 top-1/2 -translate-y-1/2"
              content={tooltipContent}
              side={isMobileMode ? 'top' : 'right'}
            />
          )}
          <FieldsWrapper>
            {isMMddYYYYFormat ? (
              <>
                <DateSelectWrapper>
                  <DateSelect
                    disabled={disabled}
                    options={Object.values(MONTHS())}
                    value={month}
                    onChange={handleMonthChange}
                    invalid={isInvalid}
                    name={`${name}-month`}
                    placeholder={t('month')}
                    isUSAFormat={true}
                    isFocused={isFocused}
                    ref={usaSelectRef}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        inputRef.current?.focus();
                      }
                    }}
                  />
                </DateSelectWrapper>
                <DayInputWrapper $isUSAFormat={true}>
                  <DateInput
                    disabled={disabled}
                    onChange={handleDayChange}
                    placeholder={t('day')}
                    value={String(day || '')}
                    type="number"
                    name={`${name}-day`}
                    invalid={isInvalid}
                    ref={inputRef}
                    onFocus={() => {
                      onFieldFocus?.('day');
                    }}
                    onBlur={() => {
                      onFieldBlur?.('day');
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        inputYearRef.current?.focus();
                      }
                    }}
                  />
                </DayInputWrapper>
              </>
            ) : (
              <>
                <DayInputWrapper $isUSAFormat={isMMddYYYYFormat}>
                  <DateInput
                    disabled={disabled}
                    onChange={handleDayChange}
                    placeholder={t('day')}
                    value={String(day || '')}
                    type="number"
                    name={`${name}-day`}
                    inputMode="numeric"
                    invalid={isInvalid}
                    ref={inputRef}
                    onFocus={() => {
                      setIsFocused(true);
                      onFieldFocus?.('day');
                    }}
                    onBlur={() => {
                      onFieldBlur?.('day');
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        setIsFocused(false);
                        usaSelectRef.current?.focus();
                      }
                    }}
                  />
                </DayInputWrapper>
                <DateSelectWrapper>
                  <DateSelect
                    ref={usaSelectRef}
                    disabled={disabled}
                    options={Object.values(MONTHS())}
                    value={month}
                    onChange={handleMonthChange}
                    invalid={isInvalid}
                    name={`${name}-month`}
                    placeholder={t('month')}
                    isFocused={isFocused}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        inputYearRef.current?.focus();
                      }
                    }}
                  />
                </DateSelectWrapper>
              </>
            )}
            <YearInputWrapper>
              <DateInput
                ref={inputYearRef}
                disabled={disabled}
                onChange={handleYearChange}
                placeholder={t('year')}
                value={String(year || '')}
                type="number"
                inputMode="numeric"
                name={`${name}-year`}
                invalid={isInvalid || isYearInvalid}
                onFocus={() => {
                  onFieldFocus?.('year');
                }}
                onBlur={() => {
                  onFieldBlur?.('year');
                  setIsFocused(false);
                }}
                onKeyDown={handleKeyDown}
              />
            </YearInputWrapper>
          </FieldsWrapper>
        </Wrapper>
        {error && (
          <FieldErrorMessage
            disabled={disabled || false}
            data-testid={`${name}-error`}
            className="kiosko:text-kiosko-xs kiosko:bottom-[-44px]"
          >
            {error as string}
          </FieldErrorMessage>
        )}
      </>
    );
  },
);

export {Datepicker};
